import { generatePositionDefaults } from '../compensationPartner';

/**
 * Data Format Documentation:
 * 
 * 1. INTEGRATED FORMAT (from IntegratedSalaryService):
 *    - Detection: Has 'compensationPartner' and 'name' at root level
 *    - Source: integrated_salary_report_*.json files
 *    - Status: ACTIVELY USED
 * 
 * 2. NEW FORMAT (individual position reports):
 *    - Detection: Has 'data.compensationPartner' structure
 *    - Source: data/integrated_reports/positions/*.json
 *    - Status: ACTIVELY USED
 * 
 * Both formats are normalized using the same logic since they contain
 * the same data structure, just at different nesting levels.
 */

/**
 * Main entry point for normalizing compensation data from different file formats
 * @param {object} rawData - Raw data from JSON file
 * @returns {object|null} Normalized position data with consistent structure
 */
export function normalizeCompensationData(rawData) {
  if (!rawData || typeof rawData !== 'object') {
    console.error('Invalid data provided to normalizeCompensationData');
    return null;
  }

  // Extract data based on format - both formats have the same structure,
  // just at different nesting levels
  let dataContainer = null;
  let formatType = null;

  // Integrated format from integrated_salary_report (data at root)
  if (rawData.compensationPartner && rawData.name) {
    dataContainer = rawData;
    formatType = 'INTEGRATED';
  }
  // New format with nested data structure
  else if (rawData.data && rawData.data.compensationPartner) {
    dataContainer = rawData.data;
    formatType = 'NEW';
  }

  if (dataContainer) {
    console.log(`Detected ${formatType} format`);
    return normalizeModernFormat(dataContainer, formatType);
  }
  
  console.error('Unknown data format. Expected INTEGRATED or NEW format', {
    hasCompensationPartner: !!rawData.compensationPartner,
    hasName: !!rawData.name,
    hasData: !!rawData.data,
    structure: Object.keys(rawData).slice(0, 5) // Show first 5 keys for debugging
  });
  return null;
}

/**
 * Normalizes both integrated and new format data (unified approach)
 * @param {object} dataContainer - Container with compensationPartner, kununu, gehaltDe data
 * @param {string} formatType - Either 'INTEGRATED' or 'NEW' for logging purposes
 * @returns {object} Normalized position data
 */
function normalizeModernFormat(dataContainer, formatType) {
  const cpData = dataContainer.compensationPartner;
  const kununuData = dataContainer.kununu;
  const gehaltDeData = dataContainer.gehaltDe;
  const comparison = dataContainer.comparison; // May exist in NEW format
  
  // Validate required data
  if (!cpData || !cpData.metadata) {
    console.error('Invalid compensationPartner data structure');
    return null;
  }
  
  // Generate intelligent defaults for missing fields
  const defaults = generatePositionDefaults(
    cpData.metadata.positionsBezeichnung, 
    cpData.metadata.positionGroupId
  );
  
  return {
    // Basic position info
    positionsBezeichnung: cpData.metadata.positionsBezeichnung,
    department: cpData.metadata.department,
    mindestausbildungId: cpData.metadata.mindestausbildungId,
    mindesterfahrungId: cpData.metadata.mindesterfahrungId,
    positionGroupId: cpData.metadata.positionGroupId,
    teamgroesseId: cpData.metadata.teamgroesseId,
    budget: cpData.metadata.budget,
    budgetVformat: cpData.metadata.budgetVformat || null,
    umsatz: cpData.metadata.umsatz,
    umsatzVformat: cpData.metadata.umsatzVformat || null,
    monatsgehaltFormat: cpData.metadata.monatsgehaltFormat,
    
    // Use intelligent defaults if not available
    kommunikation: cpData.metadata.kommunikation || defaults.kommunikation,
    komplexität: cpData.metadata.komplexität || defaults.komplexität,
    hasGeneratedDefaults: !cpData.metadata.kommunikation || !cpData.metadata.komplexität,
    
    // Preserve compensationPartner metadata structure for access to stellen data
    compensationPartner: {
      metadata: {
        stellen: cpData.metadata.stellen || []
      }
    },
    
    // Salary information
    salary: normalizeSalaryData(cpData),
    
    // Market data sources
    kununu: kununuData ? normalizeKununuData(kununuData) : null,
    gehaltDe: gehaltDeData ? normalizeGehaltDeData(gehaltDeData) : null,
    
    // Use existing comparison or calculate new one
    comparison: comparison || createComparisonData(cpData.currentBasic, kununuData),
    
    // Metadata
    _normalized: {
      format: formatType,
      timestamp: new Date().toISOString()
    }
  };
}

/**
 * Normalizes salary data into consistent structure
 * @param {object} cpData - Compensation Partner data
 * @returns {object} Normalized salary object
 */
function normalizeSalaryData(cpData) {
  return {
    peerigonCurrentBasic: cpData.currentBasic,
    medianBasic: cpData.medianBasic,
    peerigonCurrentTotalCash: cpData.currentTotalCash,
    medianTotalCash: cpData.medianTotalCash,
    basicPercentage: cpData.percentageVsMedian,
    totalCashPercentage: cpData.totalCashPercentage,
    medianBasicMonthlyToIncrease: cpData.medianBasicMonthlyToIncrease
  };
}

/**
 * Creates comparison data between current salary and market data
 * @param {number} currentBasic - Current basic salary
 * @param {object} kununuData - Kununu market data
 * @returns {object|null} Comparison data or null if insufficient data
 */
function createComparisonData(currentBasic, kununuData) {
  if (!kununuData || !currentBasic || !kununuData.derivedSalary?.derivedSalary) {
    return null;
  }
  
  const kununuSalary = kununuData.derivedSalary.derivedSalary;
  
  // Validate numeric values
  if (typeof currentBasic !== 'number' || typeof kununuSalary !== 'number' || kununuSalary === 0) {
    return null;
  }
  
  const difference = currentBasic - kununuSalary;
  const percentageDifference = Math.round((difference / kununuSalary) * 100);
  
  return {
    currentVsKununu: difference,
    percentageDifference,
    status: currentBasic > kununuSalary ? 'above' : 'below'
  };
}

/**
 * Normalizes Kununu data structure
 * @param {object} kununuData - Raw Kununu data
 * @returns {object} Normalized Kununu data
 */
function normalizeKununuData(kununuData) {
  return {
    averageSalary: kununuData.derivedSalary?.derivedSalary || kununuData.averageSalary,
    salaryRange: kununuData.salaryRange,
    position: kununuData.position,
    dataPoints: kununuData.dataPoints,
    managementSalary: kununuData.managementSalary,
    derivedSalary: kununuData.derivedSalary,
    dataAvailability: kununuData.dataAvailability,
    fullData: kununuData.fullData,
    config: kununuData.config,
    // Preserve byResponsibility for advanced comparisons
    byResponsibility: kununuData.byResponsibility || kununuData.fullData?.byResponsibility
  };
}

/**
 * Normalizes Gehalt.de data structure
 * @param {object} gehaltDeData - Raw Gehalt.de data
 * @returns {object} Normalized Gehalt.de data
 */
function normalizeGehaltDeData(gehaltDeData) {
  return {
    averageSalary: gehaltDeData.derivedSalary?.derivedSalary || 
                   gehaltDeData.averageSalary || 
                   gehaltDeData.fullData?.overview?.averageSalary,
    salaryRange: gehaltDeData.salaryRange || gehaltDeData.fullData?.overview?.salaryRange,
    position: gehaltDeData.position || gehaltDeData.fullData?.position?.title,
    dataPoints: gehaltDeData.dataPoints || gehaltDeData.fullData?.overview?.dataPoints,
    experienceLevels: gehaltDeData.experienceLevels || gehaltDeData.fullData?.experienceLevels,
    byResponsibility: gehaltDeData.byResponsibility || gehaltDeData.fullData?.byResponsibility,
    managementSalary: gehaltDeData.managementSalary,
    derivedSalary: gehaltDeData.derivedSalary,
    dataAvailability: gehaltDeData.dataAvailability,
    fullData: gehaltDeData.fullData || gehaltDeData,
    config: gehaltDeData.config
  };
}

/**
 * Validates that required fields are present in normalized data
 * @param {object} data - Normalized data
 * @returns {boolean} True if valid, false otherwise
 */
export function validateNormalizedData(data) {
  if (!data || typeof data !== 'object') {
    console.warn('Invalid data object provided to validateNormalizedData');
    return false;
  }
  
  const requiredFields = [
    'positionsBezeichnung',
    'department',
    'salary',
    'salary.peerigonCurrentBasic',
    'salary.medianBasic'
  ];
  
  for (const field of requiredFields) {
    try {
      const value = field.split('.').reduce((obj, key) => obj?.[key], data);
      if (value === undefined || value === null) {
        console.warn(`Missing required field: ${field}`);
        return false;
      }
    } catch (error) {
      console.warn(`Error accessing field ${field}:`, error);
      return false;
    }
  }
  
  return true;
}

/**
 * Gets a summary of the data format for debugging
 * @param {object} data - Normalized data
 * @returns {object} Summary of data contents
 */
export function getDataSummary(data) {
  if (!data) return null;
  
  return {
    position: data.positionsBezeichnung,
    department: data.department,
    hasSalary: !!data.salary,
    hasKununu: !!data.kununu,
    hasGehaltDe: !!data.gehaltDe,
    hasStellen: !!(data.compensationPartner?.metadata?.stellen?.length),
    stellenCount: data.compensationPartner?.metadata?.stellen?.length || 0,
    stellenDetails: data.compensationPartner?.metadata?.stellen?.map(s => s.selectJob),
    hasGeneratedDefaults: data.hasGeneratedDefaults,
    format: data._normalized?.format,
    normalizedAt: data._normalized?.timestamp
  };
}

/**
 * Checks if data appears to be already normalized
 * @param {object} data - Data to check
 * @returns {boolean} True if data appears to be already normalized
 */
export function isDataNormalized(data) {
  return !!(
    data && 
    data.salary && 
    data.salary.peerigonCurrentBasic !== undefined &&
    data._normalized
  );
}